/**
 * useAdvisorInviteFunnelStatus — shared read of the owner's OWN sent
 * advisor-invite funnel state (WL-GTM-7 + Case-Study-Engine-Hook,
 * modules-loop-b.md §WL-GTM-7 / §"Case-Study-Engine-Hook").
 *
 * Wraps {@link listMyAdvisorInvites} once per `enabled` toggle so several
 * Loop-B growth surfaces (the "Kanzlei einladen"-CTA in its three
 * placements, the Case-Study testimonial ask) can share ONE fetch instead
 * of each re-querying the `listMyAdvisorInvites` callable independently.
 *
 * Fail-closed by design for `canShowInviteCta`: while the list is loading,
 * has not resolved yet, or the fetch fails unexpectedly, `canShowInviteCta`
 * stays `false` — a CTA that invites the user to "invite your Kanzlei" must
 * never render while we do not yet positively know there is no
 * pending/accepted invite already, otherwise a returning user could be
 * nudged to send a duplicate invite. This is a UX-safety default, not a
 * security control (mirrors the "fail-open on storage errors, fail-closed
 * on unknown state" split used across `paywallTrigger4Suppression.ts`).
 *
 * `listMyAdvisorInvites()` itself already degrades to `{ invites: [] }` on
 * `failed-precondition` (flag off) — this hook additionally swallows any
 * OTHER unexpected error (network, etc.) into an `error` flag rather than
 * throwing, since every consumer is a best-effort growth nicety that must
 * never break page rendering.
 *
 * @module hooks/useAdvisorInviteFunnelStatus
 */

import { useEffect, useState } from 'react';
import { listMyAdvisorInvites, SentAdvisorInvite } from '../api/advisorInviteApi';
import logger from '../utils/logger';

/** Return shape of {@link useAdvisorInviteFunnelStatus}. */
export interface AdvisorInviteFunnelStatus {
  /** `true` while the invite list is being fetched (or not yet started). */
  loading: boolean;
  /** `true` if the fetch failed unexpectedly (not the normal flag-off empty case). */
  error: boolean;
  /**
   * `true` once at least one of the owner's sent invites has `status ===
   * 'accepted'` — i.e. an active Kanzlei link exists. Used as the
   * Case-Study-Engine-Hook milestone ("erste akzeptierte
   * Kanzlei-Verknüpfung").
   */
  hasAcceptedInvite: boolean;
  /**
   * Fail-closed readiness signal for the WL-GTM-7 "Kanzlei einladen"-CTA:
   * `true` ONLY once the invite list resolved successfully AND holds no
   * `pending`/`accepted` entry (nothing left to invite for / already
   * invited). `false` while loading, on error, or once an active/pending
   * invite exists.
   */
  canShowInviteCta: boolean;
}

/**
 * @param enabled - gate for the underlying fetch (e.g. `isLoopBBeta && isOwnScope
 *   && activeRole !== 'advisor'`). When `false`, no callable is invoked and the
 *   hook resolves to the fail-closed defaults (`loading:false`, `canShowInviteCta:false`).
 */
export function useAdvisorInviteFunnelStatus(enabled: boolean): AdvisorInviteFunnelStatus {
  const [invites, setInvites] = useState<SentAdvisorInvite[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      // Not eligible to begin with — no need to fetch; keep prior result (if
      // any) but don't flip loading/error, since the caller won't use them.
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    listMyAdvisorInvites()
      .then((res) => {
        if (cancelled) return;
        setInvites(res.invites);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.debug('useAdvisorInviteFunnelStatus: fetch failed (fail-closed)', err);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const list = invites ?? [];
  const hasAcceptedInvite = list.some((invite) => invite.status === 'accepted');
  const hasActiveOrPendingInvite = list.some(
    (invite) => invite.status === 'accepted' || invite.status === 'pending',
  );

  return {
    loading,
    error,
    hasAcceptedInvite,
    canShowInviteCta: enabled && !loading && !error && invites !== null && !hasActiveOrPendingInvite,
  };
}
